import { Badge, Icon, Skeleton } from "@elit-hair/elit-ui";
import { Link, Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TbCalendarEvent, TbCalendarTime, TbListCheck } from "react-icons/tb";
import z from "zod";

import { cn } from "@/lib/utils";
import { useEventType } from "@/queries/event-type.queries";
import { APP_ROUTES_TO } from "@/utils/routes";

const EventParams = z.object({
    id: z.string(),
});

export const Route = createFileRoute("/events/$id")({
    component: EventTypeLayout,
    params: {
        parse: (rawParams) => {
            const result = EventParams.safeParse(rawParams);

            if (!result.success) {
                throw redirect({
                    to: APP_ROUTES_TO.EVENTS,
                    state: {
                        toast: {
                            type: "error",
                            message: "invalid_route_parameters",
                        },
                    },
                });
            }

            return result.data;
        },
        stringify: (params) => ({
            id: params.id,
        }),
    },
});

type EventSubnavItem = {
    to: typeof APP_ROUTES_TO.EVENT_RULES | typeof APP_ROUTES_TO.EVENT_SLOTS | typeof APP_ROUTES_TO.EVENT_BOOKINGS;
    labelKey: string;
    icon: typeof TbListCheck;
    match: string;
};

const SUBNAV_ITEMS: EventSubnavItem[] = [
    { to: APP_ROUTES_TO.EVENT_RULES, labelKey: "event_rules", icon: TbListCheck, match: "/rules" },
    { to: APP_ROUTES_TO.EVENT_SLOTS, labelKey: "bookable_slots", icon: TbCalendarTime, match: "/slots" },
    { to: APP_ROUTES_TO.EVENT_BOOKINGS, labelKey: "event_bookings", icon: TbCalendarEvent, match: "/bookings" },
];

function EventTypeLayout() {
    const { t } = useTranslation();
    const { id } = Route.useParams();
    const { data: eventType, isPending } = useEventType(id);
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const description = eventType?.description?.trim();
    const isActive = eventType?.isActive !== false;

    return (
        <div className="flex h-0 min-h-0 flex-1 flex-col gap-4">
            <div className="space-y-3">
                {isPending ? (
                    <div className="space-y-2">
                        <Skeleton className="h-7 w-56" />
                        <Skeleton className="h-4 w-72" />
                    </div>
                ) : (
                    <div className="min-w-0 space-y-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                            {eventType?.color ? (
                                <span
                                    className="size-2.5 shrink-0 rounded-full"
                                    style={{ backgroundColor: eventType.color }}
                                    aria-hidden
                                />
                            ) : null}
                            <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
                                {eventType?.title ?? t("events")}
                            </h1>
                            {eventType?.slug ? (
                                <code className="truncate text-sm text-muted-foreground">/{eventType.slug}</code>
                            ) : null}
                            {eventType ? (
                                <Badge
                                    label={isActive ? t("active") : t("inactive")}
                                    variant={isActive ? "success" : "danger"}
                                />
                            ) : null}
                        </div>
                        {description ? <p className="max-w-2xl text-sm text-muted-foreground">{description}</p> : null}
                    </div>
                )}

                {isPending ? (
                    <Skeleton className="h-10 w-full max-w-md rounded-xl" />
                ) : (
                    <nav
                        className="inline-flex w-fit max-w-full flex-wrap gap-1 rounded-xl border border-border/60 bg-muted/30 p-1"
                        aria-label={t("events")}
                    >
                        {SUBNAV_ITEMS.map((item) => {
                            const isTabActive = pathname.includes(item.match);

                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    params={{ id }}
                                    className={cn(
                                        "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                                        isTabActive
                                            ? "bg-background text-foreground shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    <Icon icon={item.icon} className="size-4" />
                                    {t(item.labelKey)}
                                </Link>
                            );
                        })}
                    </nav>
                )}
            </div>

            <div className="flex h-0 min-h-0 flex-1 flex-col">
                <Outlet />
            </div>
        </div>
    );
}
